import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Users, Lightbulb, Target } from 'lucide-react';
import { careersContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';

const CareersPage = () => {
  const { language, t, pick } = useLanguage();
  useDocumentTitle(language === 'fr' ? 'Carrières' : 'Careers');

  const valuesByLang = {
    en: [
      { icon: Target, title: 'Responsibility', desc: 'Own your work and its impact' },
      { icon: Lightbulb, title: 'Clarity', desc: 'Think clearly, communicate effectively' },
      { icon: Briefcase, title: 'Execution', desc: 'Deliver real results that matter' },
      { icon: Users, title: 'Collaboration', desc: 'Work together across boundaries' }
    ],
    fr: [
      { icon: Target, title: 'Responsabilité', desc: 'Assumez votre travail et son impact' },
      { icon: Lightbulb, title: 'Clarté', desc: 'Pensez clairement, communiquez efficacement' },
      { icon: Briefcase, title: 'Exécution', desc: 'Livrez des résultats qui comptent' },
      { icon: Users, title: 'Collaboration', desc: 'Travaillez ensemble sans frontières' }
    ]
  };
  const values = valuesByLang[language];

  const e = {
    en: { whatWeValue: 'What We Value', ourCulture: 'Our Culture', joinTeam: 'Join Our Team', buildMeaningful: 'Build Something Meaningful', joinIntro: "We're looking for builders, operators and thinkers who want to work on real projects with long-term impact across Africa and beyond.", sendApp: 'Send your application to:', placeholder: '(placeholder)', preferPartner: 'Prefer to Partner Instead?', preferSub: 'Explore strategic partnership opportunities with Smartworld Group.', viewPart: 'View Partnerships' },
    fr: { whatWeValue: 'Ce que nous valorisons', ourCulture: 'Notre culture', joinTeam: 'Rejoignez notre équipe', buildMeaningful: 'Bâtissez quelque chose qui compte', joinIntro: "Nous recherchons des bâtisseurs, opérateurs et penseurs qui souhaitent travailler sur des projets concrets à impact long terme en Afrique et au-delà.", sendApp: 'Envoyez votre candidature à :', placeholder: '(temporaire)', preferPartner: 'Plutôt un partenariat ?', preferSub: 'Explorez les opportunités de partenariat stratégique avec Smartworld Group.', viewPart: 'Voir les partenariats' }
  }[language];

  return (
    <div className="pt-20">
      <section className="section-padding-lg bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] border border-[#5B7A8A] rounded-full"></div>
        </div>
        <div className="container-swg relative z-10">
          <div className="max-w-4xl">
            <span className="caption text-[#5B7A8A] block mb-4">{pick(careersContent.title)}</span>
            <h1 className="heading-1 text-white mb-8">{pick(careersContent.headline)}</h1>
            <div className="accent-line mb-8"></div>
            <p className="body-large max-w-2xl mb-6">{pick(careersContent.text)}</p>
            <p className="text-[#5B7A8A] font-medium text-lg">{pick(careersContent.values)}</p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="text-center mb-16">
            <span className="caption text-[#5B7A8A] block mb-4">{e.whatWeValue}</span>
            <h2 className="heading-2 text-white">{e.ourCulture}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="swg-card rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#5B7A8A]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{value.title}</h3>
                <p className="text-[#9AACB8] text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#1a1c1b]">
        <div className="container-swg">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption text-[#5B7A8A] block mb-4">{e.joinTeam}</span>
            <h2 className="heading-2 text-white mb-8">{e.buildMeaningful}</h2>
            <div className="accent-line mx-auto mb-8"></div>

            <div className="swg-card rounded-2xl p-10">
              <p className="body-large mb-8">{e.joinIntro}</p>
              <div className="flex flex-col items-center gap-4">
                <p className="text-[#9AACB8]">{e.sendApp}</p>
                <a href={`mailto:${careersContent.email}`} className="text-[#5B7A8A] text-xl font-semibold hover:underline">{careersContent.email}</a>
                <span className="text-[#9AACB8] text-sm">{e.placeholder}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#3d5a6a]/20 border-y border-[rgba(91,122,138,0.5)]">
        <div className="container-swg">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-3 text-white mb-2">{e.preferPartner}</h2>
              <p className="text-[#9AACB8]">{e.preferSub}</p>
            </div>
            <Link to="/partnerships" className="btn-primary">
              {e.viewPart}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
